import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { type MutableRefObject, useContext, useEffect, useRef } from "react";
import { UNSAFE_DataRouterContext, useBlocker } from "react-router-dom";

import { DashboardRecord, isDashboardRecord } from "@/shared/domain/dashboards";
import { useUpdateDashboard } from "@/shared/domain/dashboards/hooks";
import { Button } from "@/shared/foundation/primitives";
import {
  RobotoDashboardRevisionConflictException,
  userFacingErrorMessage,
} from "@/shared/http";

import { DashboardDefinition } from "../DashboardHeader/DashboardParams";

import styles from "./DashboardLeaveGuard.module.css";

export interface DashboardLeaveGuardProps {
  /** True while the loaded dashboard's definition differs from the live
   *  view state in LAYOUT — sections/cards, ignoring range/period (the
   *  owner's `isLayoutDirty`, the blocking tier of dirty; a params-only
   *  tweak is saveable but never blocks leaving). The guard is inert when
   *  false. */
  when: boolean;
  /** When true, the user's edits have been persisted under a different
   *  dashboard (e.g. "save a copy" from the conflict dialog). The guard
   *  should not block the navigation that switches to that copy. Read
   *  synchronously inside the blocker condition (ref, not state) so the
   *  value is visible within the same call stack as the navigate(). */
  savedElsewhereRef: MutableRefObject<boolean>;
  /** The loaded saved-dashboard record — the Save target. `when` is only
   *  ever true for a loaded dashboard (an unsaved view has no record to be
   *  dirty against; its edits persist as the localStorage draft and are
   *  offered back by the restore prompt instead). */
  dashboard: DashboardRecord | null;
  /** Snapshot of current view state, persisted on Save. */
  currentDefinition: DashboardDefinition;
  /** The revision the canvas was seeded from, sent with the save so a stale
   *  write is rejected rather than clobbering someone else's edit. Null when no
   *  record seeded the canvas, in which case Save is inert (`when` already
   *  implies a loaded dashboard, so this is belt-and-braces). */
  baseRevision: number | null;
  /** A Save succeeded — the passed record now matches the live view. The owner
   *  clears its localStorage draft and records the saved dashboard as the
   *  remembered choice. */
  onSaved: (saved: DashboardRecord) => void;
  /** The Save was rejected because someone else changed the dashboard first.
   *  The owner shows the conflict dialog; navigation stays blocked, because the
   *  user's work is still unpersisted. */
  onRevisionConflict: (serverRecord: DashboardRecord) => void;
  /** The user chose to discard — the owner clears its localStorage draft so
   *  the edits don't resurrect on the next visit. */
  onDiscard: () => void;
}

/**
 * Blocks leaving the metrics dashboard page while it has unsaved layout
 * edits.
 *
 * Two layers:
 *
 * - In-app navigation: react-router's `useBlocker` (the app router is a
 *   `createBrowserRouter` data router) intercepts pathname changes and shows
 *   a Save / Discard / Keep-editing dialog. Same-path navigations (the
 *   range/period/dashboard search params this page writes) are never
 *   blocked.
 * - Tab close / reload: a native `beforeunload` handler, registered only
 *   while dirty (the browser allows no custom UI there).
 *
 * `useBlocker` throws outside a data router, so the blocking half mounts
 * only when a data-router context is present. Component tests that render
 * the dashboard inside a plain `MemoryRouter` get the no-op version.
 */
export function DashboardLeaveGuard(props: DashboardLeaveGuardProps) {
  const { when } = props;

  useEffect(() => {
    if (!when) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome only shows the native confirm when returnValue is set.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [when]);

  const dataRouterContext = useContext(UNSAFE_DataRouterContext);
  if (dataRouterContext === null) {
    return null;
  }
  return <RouteLeaveBlocker {...props} />;
}

/** The data-router half: registers the blocker and renders the dialog while
 *  a navigation is held. Split out so `useBlocker` is only ever called under
 *  a data router. */
function RouteLeaveBlocker({
  when,
  savedElsewhereRef,
  dashboard,
  currentDefinition,
  baseRevision,
  onSaved,
  onRevisionConflict,
  onDiscard,
}: DashboardLeaveGuardProps) {
  const updateMutation = useUpdateDashboard();
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when &&
      !savedElsewhereRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );
  // The save mutation's onSuccess fires after an async round-trip, by which
  // time the blocker captured at mutate() time may have been reset (e.g. the
  // dialog dismissed) — react-router throws an invariant when `proceed()` is
  // called on a blocker that is no longer blocking. Track the live blocker so
  // the callback can check its CURRENT state instead of the stale capture.
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  if (blocker.state !== "blocked") {
    return null;
  }

  const pending = updateMutation.isPending;

  // Dismissing the dialog (Escape / backdrop click / "Keep editing") resets
  // the blocker. That must never happen while a save is in flight: the
  // pending save's outcome (proceed on success, show the error on failure)
  // still needs the blocked navigation.
  const handleClose = () => {
    if (pending) {
      return;
    }
    // This component stays mounted between blocks, so mutation state must be
    // cleared on dismissal or a failed save's error would still be showing
    // the next time a navigation opens the dialog.
    updateMutation.reset();
    blocker.reset();
  };

  const handleSave = () => {
    if (dashboard === null || baseRevision === null) {
      // `when` implies a loaded dashboard, which implies a seeded base
      // revision; belt-and-braces only. A definition write must never go out
      // without one.
      return;
    }
    updateMutation.mutate(
      {
        dashboardId: dashboard.dashboard_id,
        updates: {
          dashboard_definition: currentDefinition,
          base_revision: baseRevision,
        },
      },
      {
        onSuccess: (record) => {
          onSaved(record);
          const current = blockerRef.current;
          if (current.state === "blocked") {
            current.proceed();
          }
        },
        onError: (error) => {
          // Deliberately does NOT proceed: the save failed, so the user's work
          // is still unpersisted and leaving would strand it. The conflict
          // dialog takes over and both of its doors end with the navigation
          // resolved one way or the other.
          if (error instanceof RobotoDashboardRevisionConflictException) {
            const serverRecord = error.currentDashboard;
            if (isDashboardRecord(serverRecord)) {
              onRevisionConflict(serverRecord);
            }
          }
        },
      },
    );
  };

  const handleDiscard = () => {
    updateMutation.reset();
    onDiscard();
    blocker.proceed();
  };

  return (
    <Dialog open onClose={handleClose}>
      <DialogTitle>Unsaved dashboard changes</DialogTitle>
      <DialogContent>
        <div className={styles.dialogBody}>
          Your changes to <strong>{dashboard?.name ?? "this dashboard"}</strong>{" "}
          haven&apos;t been saved. Save them before leaving?
          {/* MUI Alert carries role="alert", so the failure is announced to
           * screen readers when it appears mid-dialog — the same pattern as
           * the dashboard's load-error surface (MetricsDashboard.tsx) and
           * the visualizer's SaveLayoutConfirmation. */}
          {updateMutation.error &&
            !(
              updateMutation.error instanceof
              RobotoDashboardRevisionConflictException
            ) && (
              <Alert severity="error" className={styles.error}>
                {/* A conflict owns its own dialog, so it is excluded here
                 * rather than duplicated alongside it. */}
                {userFacingErrorMessage(
                  updateMutation.error,
                  "Failed to save dashboard. Please try again.",
                )}
              </Alert>
            )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={pending}>
          Keep editing
        </Button>
        <Button variant="outlined" onClick={handleDiscard} disabled={pending}>
          Discard changes
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={pending}>
          {pending ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
